import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../../context/Context';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import '../../../assets/css/buttons.css';
import TerminalScreenshotsViewer from './TerminalScreenshotsViewer';

import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RestartAlt as RestartAltIcon,
  PowerSettingsNew as RebootIcon,
  PhotoLibrary as ScreenshotsIcon,
} from '@mui/icons-material/';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';

function ModalTerminals() {
  const { t } = useTranslation();

  const {
    updateTerminals,
    terminals,
    updateShowModalTerminals,
    updateFocusOnMainScreen,
    updateIsLoadingScreen,
  } = useContext(AppContext);

  const terminalsRef = useRef(terminals);
  useEffect(() => {
    terminalsRef.current = terminals;
    console.log('Updated terminalsRef:', terminalsRef.current);
  }, [terminals]);

  const [currentSelectedTerminal, setCurrentSelectedTerminal] = useState(null);
  const [screenshotTerminal, setScreenshotTerminal] = useState(null);

  const TYPE_LABELS = {
    flash: t('sales.terminalsTypeFlash'),
    kiosk: t('sales.terminalsTypeKiosk'),
    terminal: t('sales.terminalsTypeTerminal'),
  };

  const handleConfirm = () => {
    updateTerminals(false);
    updateShowModalTerminals(false);
    updateFocusOnMainScreen(true);
  };

  const getLastOctet = (ip) => ip.split('.').pop();

  const handleActionSelect = (ip, command, type) => {
    setCurrentSelectedTerminal({ ip, command, type });
  };

  const executeCommandAll = async (command) => {
    const rows = terminalsRef.current;
    let hasError = false;
    for (const row of rows) {
      try {
        await fetch(`https://${row.ip}:4433/${command}`, { method: 'GET' });
      } catch {
        hasError = true;
      }
    }
    if (hasError) {
      toast.error(t('error.error'));
    } else {
      toast.success(t('sales.terminalsActionSuccess'));
    }
  };

  const handleActionConfirm = async () => {
    const { ip, command, all } = currentSelectedTerminal;
    setCurrentSelectedTerminal(null);
    updateIsLoadingScreen(true);
    if (all) {
      await executeCommandAll(command);
    } else {
      try {
        await fetch(`https://${ip}:4433/${command}`, { method: 'GET' });
        toast.success(t('sales.terminalsActionSuccess'));
      } catch {
        toast.error(t('error.error'));
      }
    }
    updateIsLoadingScreen(false);
  };

  const renderConfirmation = () => {
    const { ip, command, type, all } = currentSelectedTerminal;
    const isSoftReset = command === 'KillChrome';

    let title, text;
    if (all) {
      title = isSoftReset
        ? t('sales.terminalsConfirmSoftResetAllTitle')
        : t('sales.terminalsConfirmRebootAllTitle');
      text = isSoftReset
        ? t('sales.terminalsConfirmSoftResetAllText')
        : t('sales.terminalsConfirmRebootAllText');
    } else {
      const id = getLastOctet(ip);
      const deviceType = TYPE_LABELS[type] ?? TYPE_LABELS.terminal;
      title = isSoftReset
        ? t('sales.terminalsConfirmSoftResetTitle', { id, deviceType })
        : t('sales.terminalsConfirmRebootTitle', { id, deviceType });
      text = isSoftReset
        ? t('sales.terminalsConfirmSoftResetText', { id, deviceType })
        : t('sales.terminalsConfirmRebootText', { id, deviceType });
    }

    return (
      <div
        className="react-confirm-alert-body"
        style={{ width: '700px', color: '#000' }}
      >
        <h1>{title}</h1>
        <p>{text}</p>

        <div
          className="react-confirm-alert-button-group"
          style={{ marginTop: '55px' }}
        >
          <button
            className="btn-custom-yes"
            onClick={handleActionConfirm}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <CheckCircleIcon style={{ fontSize: '1.2em' }} />
            {t('sales.confirm')}
          </button>
          <button
            className="btn-custom-no"
            onClick={() => setCurrentSelectedTerminal(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <CancelIcon style={{ fontSize: '1.2em' }} />
            {t('sales.cancel')}
          </button>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const rows = terminalsRef.current;

    if (!Array.isArray(rows) || rows.length === 0) {
      return <Box p={2}>{t('sales.noData')}</Box>;
    }

    const sorted = [...rows].sort((a, b) => Number(getLastOctet(a.ip)) - Number(getLastOctet(b.ip)));

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <TableContainer
          className="sp-scroll"
          sx={{
            flex: 1,
            overflow: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              '& thead th': {
                fontWeight: 'bold',
                backgroundColor: '#f0f0f0',
              },
              '& tbody tr:nth-of-type(odd)': {
                backgroundColor: 'rgba(0,0,0,0.02)',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="center" style={{ color: '#000' }}>
                  {t('sales.terminalsID')}
                </TableCell>
                <TableCell align="center" style={{ color: '#000' }}>
                  {t('sales.terminalsType')}
                </TableCell>
                <TableCell align="center" style={{ color: '#000' }}>
                  {t('sales.terminalsSoftReset')}
                </TableCell>
                <TableCell align="center" style={{ color: '#000' }}>
                  {t('sales.terminalsReboot')}
                </TableCell>
                <TableCell align="center" style={{ color: '#000' }}>
                  {t('sales.terminalsScreenshots')}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sorted.map((row) => {
                const TYPE_MAP = {
                  flash: {
                    label: t('sales.terminalsTypeFlash'),
                    color: 'warning',
                  },
                  kiosk: {
                    label: t('sales.terminalsTypeKiosk'),
                    color: 'success',
                  },
                  terminal: {
                    label: t('sales.terminalsTypeTerminal'),
                    color: 'primary',
                  },
                };
                const chip = TYPE_MAP[row.type] ?? TYPE_MAP.terminal;

                return (
                  <TableRow key={row.ip}>
                    <TableCell align="center" style={{ color: '#000' }}>
                      {getLastOctet(row.ip)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={chip.label}
                        size="small"
                        color={chip.color}
                        variant="outlined"
                        sx={{ fontWeight: 600, minWidth: 80 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('sales.terminalsSoftReset')}>
                        <IconButton
                          color="warning"
                          onClick={() =>
                            handleActionSelect(row.ip, 'KillChrome', row.type)
                          }
                          size="medium"
                        >
                          <RestartAltIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('sales.terminalsReboot')}>
                        <IconButton
                          color="error"
                          onClick={() =>
                            handleActionSelect(row.ip, 'Reboot', row.type)
                          }
                          size="medium"
                        >
                          <RebootIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('sales.terminalsScreenshots')}>
                        <IconButton
                          color="info"
                          onClick={() =>
                            setScreenshotTerminal({ ip: row.ip, type: row.type })
                          }
                          size="medium"
                        >
                          <ScreenshotsIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const hasTerminals =
    Array.isArray(terminalsRef.current) && terminalsRef.current.length > 0;

  if (currentSelectedTerminal) {
    return (
      <div id="react-confirm-alert">
        <div className="react-confirm-alert-overlay">
          <div className="react-confirm-alert">{renderConfirmation()}</div>
        </div>
      </div>
    );
  }

  if (screenshotTerminal) {
    const { ip, type } = screenshotTerminal;
    return (
      <div id="react-confirm-alert">
        <div className="react-confirm-alert-overlay">
          <div className="react-confirm-alert">
            <TerminalScreenshotsViewer
              ip={ip}
              deviceType={TYPE_LABELS[type] ?? TYPE_LABELS.terminal}
              id={getLastOctet(ip)}
              onClose={() => setScreenshotTerminal(null)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="react-confirm-alert">
      <div className="react-confirm-alert-overlay">
        <div className="react-confirm-alert">
          {hasTerminals ? (
            <div
              className="react-confirm-alert-body"
              style={{ width: '700px', height: '800px', color: '#000' }}
            >
              <Paper
                elevation={3}
                sx={{
                  backgroundColor: 'white',
                  padding: 2,
                  borderRadius: 2,
                  height: '550px',
                  overflow: 'hidden',
                  color: 'black',
                }}
              >
                {renderTable()}
              </Paper>

              <div
                className="react-confirm-alert-button-group"
                style={{ marginTop: '55px' }}
              >
                <button
                  className="btn-custom-yes"
                  onClick={handleConfirm}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                  {t('sales.close')}
                </button>
                <button
                  className="btn-custom-postpone"
                  onClick={() =>
                    setCurrentSelectedTerminal({
                      command: 'KillChrome',
                      all: true,
                    })
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <RestartAltIcon style={{ fontSize: '1em' }} />
                  <span style={{ fontSize: '14px' }}>
                    {t('sales.terminalsSoftResetAll')}
                  </span>
                </button>
                <button
                  className="btn-custom-yes-and-stay"
                  onClick={() =>
                    setCurrentSelectedTerminal({ command: 'Reboot', all: true })
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <RebootIcon style={{ fontSize: '1em' }} />
                  <span style={{ fontSize: '14px' }}>
                    {t('sales.terminalsRebootAll')}
                  </span>
                </button>
              </div>
            </div>
          ) : (
            <div
              className="react-confirm-alert-body"
              style={{ width: '1000px' }}
            >
              <h1>{t('sales.terminalsTitle')}</h1>
              <p>{t('sales.noData')}</p>

              <div
                className="react-confirm-alert-button-group"
                style={{ marginTop: '55px' }}
              >
                <button
                  className="btn-custom-yes"
                  onClick={handleConfirm}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                  {t('sales.confirm')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModalTerminals;
